export interface LinhaPerfil {
  chave: string;
  rotulo: string;
  valorA: number;
  valorB: number;
  textoA: string;
  textoB: string;
  /** Quem e maior e por quanto ("Aurora +9°"), ou "Iguais". */
  textoDiferenca: string;
}

/**
 * Fracao da largura util de cada barra. Cada medida tem a propria regua (cm,
 * graus e indices nao se misturam): a maior das duas enche, a outra fica
 * proporcional, sempre a partir do zero. Valor zero ou negativo nao desenha.
 */
export function fracoesDaLinha(l: Pick<LinhaPerfil, 'valorA' | 'valorB'>): [number, number] {
  const maximo = Math.max(l.valorA, l.valorB);
  if (!(maximo > 0)) return [0, 0];
  return [Math.max(0, l.valorA) / maximo, Math.max(0, l.valorB) / maximo];
}

/**
 * Perfil de diferencas: para cada medida, uma barra de cada animal com o valor
 * escrito no fim, e quem e maior por quanto. A cor so separa A (mais forte) de
 * B; a legenda escreve quem e quem, entao nada depende so da cor.
 */
export function PerfilDiferencas({
  linhas,
  rotuloA,
  rotuloB,
}: {
  linhas: LinhaPerfil[];
  rotuloA: string;
  rotuloB: string;
}) {
  // A legenda fica parada; so a lista rola. No xl a lista enche a altura que
  // o painel vizinho ("Lado a lado") definir, sem empurrar a linha da grade.
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <ul aria-label="Legenda" className="flex shrink-0 flex-wrap gap-x-5 gap-y-1 px-4 py-3 text-[13px] sm:px-5">
        <li className="flex items-center gap-2">
          <span aria-hidden className="size-3 rounded-[3px] bg-ordem-1" />
          {rotuloA}
        </li>
        <li className="flex items-center gap-2">
          <span aria-hidden className="size-3 rounded-[3px] bg-ordem-2" />
          {rotuloB}
        </li>
      </ul>

      <div className="relative min-h-0 flex-1 xl:min-h-80">
        <ul
          tabIndex={0}
          aria-label="Medidas, da maior para a menor diferença"
          className="max-h-[70vh] space-y-3.5 overflow-y-auto px-4 pb-4 sm:px-5 xl:absolute xl:inset-0 xl:max-h-none print:static print:max-h-none print:overflow-visible"
        >
          {linhas.map((l) => {
            const [fa, fb] = fracoesDaLinha(l);
            return (
              <li
                key={l.chave}
                aria-label={`${l.rotulo}: ${rotuloA} ${l.textoA}, ${rotuloB} ${l.textoB}. ${l.textoDiferenca}`}
              >
                <div aria-hidden className="flex items-baseline justify-between gap-3 text-[13px]">
                  <span className="min-w-0 truncate font-medium text-texto">{l.rotulo}</span>
                  <span className="numero shrink-0 text-apagado">{l.textoDiferenca}</span>
                </div>
                <div aria-hidden className="mt-1 space-y-0.5">
                  <Barra fracao={fa} texto={l.textoA} cor="bg-ordem-1" />
                  <Barra fracao={fb} texto={l.textoB} cor="bg-ordem-2" />
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

/** Barra a partir do zero; sobram 5rem a direita para o valor escrito. */
function Barra({ fracao, texto, cor }: { fracao: number; texto: string; cor: string }) {
  return (
    <div className="flex items-center gap-2">
      <span
        data-barra
        className={`h-3 shrink-0 rounded-r-[4px] ${cor}`}
        style={{ width: `calc((100% - 5rem) * ${fracao})` }}
      />
      <span className="numero text-xs whitespace-nowrap text-texto">{texto}</span>
    </div>
  );
}
